import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';

interface ToolLink {
  name: string;
  description: string;
  path: string;
  icon: string;
}

const coreTools: ToolLink[] = [
  {
    name: 'Business Idea Generator',
    description: 'Generate personalized business ideas based on your interests',
    path: '/idea-generator',
    icon: '💡'
  },
  {
    name: 'Business Plan Creator',
    description: 'Create comprehensive business plans tailored to your idea',
    path: '/business-plan',
    icon: '📝'
  },
  {
    name: 'Marketing Tools',
    description: 'Access logo creation, social media content, and more',
    path: '/marketing',
    icon: '📣'
  },
  {
    name: 'Financial Tools',
    description: 'Calculate pricing, profit margins, and break-even points',
    path: '/finance',
    icon: '💰'
  }
];

const CheckoutSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      fetch(`${ENV.API_URL}/api/payment/verify-session?session_id=${sessionId}`)
        .then(res => res.json())
        .then(data => {
          setStatus(data.status || 'success');
          setEmail(data.email || '');
          setLoading(false);
          
          // Store session info in localStorage to maintain premium status
          if (data.userId) {
            localStorage.setItem('userId', data.userId);
            localStorage.setItem('userEmail', data.email || '');
            localStorage.setItem('isPremium', 'true');
          }
        })
        .catch(err => {
          console.error('Error verifying session:', err);
          toast.error('There was an issue verifying your purchase');
          setStatus('error');
          setLoading(false);
        });
    } else {
      setLoading(false);
      setStatus('missing');
    }
  }, [searchParams]);
  
  if (loading) {
    return (
      <div className="container mx-auto p-6 text-center">
        <div className="max-w-lg mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-6"></div>
            <div className="h-4 bg-gray-200 rounded mb-2 w-3/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded mb-8 w-1/2 mx-auto"></div>
          </div>
          <p className="text-lg">Verifying your purchase...</p>
        </div>
      </div>
    );
  }
  
  if (status === 'error' || status === 'missing') {
    return (
      <div className="container mx-auto p-6 text-center">
        <div className="max-w-lg mx-auto bg-white shadow-md rounded p-8">
          <div className="text-red-500 text-5xl mb-4">❌</div>
          <h1 className="text-2xl font-bold mb-4">Verification Error</h1>
          <p className="mb-6 text-gray-600">
            {status === 'missing' 
              ? 'We couldn\'t find your checkout session. This could happen if you navigated here directly.'
              : 'There was an issue verifying your purchase. Please contact our support team.'}
          </p>
          <Link to="/checkout/guest" className="bg-blue-600 text-white px-6 py-3 rounded inline-block">
            Try Again
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-10">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 mb-10">
        <div className="text-center mb-8">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold mb-4">Thank You for Your Purchase!</h1>
          
          {email && (
            <p className="mb-4 text-gray-600">
              We've sent account details to <strong>{email}</strong>
            </p>
          )}
          
          <div className="border-t border-b border-gray-200 py-6 my-6">
            <h2 className="text-xl font-semibold mb-2">What happens next?</h2>
            <p className="text-gray-600">
              1. Check your email for account details and setup instructions<br/>
              2. Use the tools below to start building your business immediately<br/>
              3. Your account is ready with full premium access - no waiting!
            </p>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold mb-6 text-center">Get Started with Your Premium Tools</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {coreTools.map((tool) => (
            <Link 
              key={tool.path}
              to={tool.path}
              className="bg-gray-50 hover:bg-blue-50 border border-gray-200 p-6 rounded-lg flex items-start transition-colors"
            >
              <div className="text-3xl mr-4">{tool.icon}</div>
              <div>
                <h3 className="font-bold text-lg mb-1">{tool.name}</h3>
                <p className="text-gray-600">{tool.description}</p>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="mt-10 text-center">
          <Link to="/" className="text-blue-600 hover:underline">
            Return to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
